import { useEffect, useState } from 'react';
import { PAGOS_POR_VENTA_MAXIMO, formatearCOP } from '@tanteo/compartido';
import type { PagoDeVenta } from '../../lib/api';
import type { Cuenta } from '../../lib/api-cuentas';
import { excedePagos, faltaPorAsignar, pagosDeReparto, repartoValido, type Reparto } from '../../lib/pago-mixto';
import { Boton, FilaAcciones, MensajeError, NumeroEnLinea } from '../../ui/base';
import { Modal } from '../../ui/Modal';
import { DIGITOS_DE_PLATA } from '../../lib/lector';

/**
 * Reparte una venta entre cuentas y solo cierra cuando lo asignado es exactamente el total, que es
 * lo que el servidor exige. Poner todo en una cuenta vale: el paso 3 la muestra seleccionada.
 */
export function PagoMixto({
  abierto,
  total,
  cuentas,
  inicial,
  aviso,
  onCerrar,
  onAceptar,
}: {
  abierto: boolean;
  total: number;
  cuentas: Cuenta[];
  /** El reparto que ya estaba puesto: se abre con él para corregirlo, no para escribirlo de nuevo. */
  inicial?: PagoDeVenta[] | null;
  /** Por qué se reabrió el reparto, cuando no lo abrió el cajero. */
  aviso?: string | null;
  onCerrar: () => void;
  onAceptar: (pagos: PagoDeVenta[]) => void;
}) {
  const [reparto, setReparto] = useState<Reparto>({});

  // Al abrir se siembra con lo que hubiera: el diálogo vive montado, así que su estado sobrevive
  // entre aperturas y sin esto mostraría lo de la vez anterior o nada.
  useEffect(() => {
    if (!abierto) return;
    setReparto(Object.fromEntries((inicial ?? []).map((g) => [g.cuentaId, g.monto])));
  }, [abierto, inicial]);
  const falta = faltaPorAsignar(total, reparto);
  const listo = repartoValido(total, reparto);

  function cerrar() {
    setReparto({});
    onCerrar();
  }

  return (
    <Modal abierto={abierto} titulo="Pago mixto" onCerrar={cerrar}>
      <div className="space-y-3">
        <MensajeError caja e2e="mixto-aviso">{aviso}</MensajeError>
        <p className="text-muted flex justify-between text-contenido">
          <span>Total a cobrar</span>
          <span className="num text-foreground font-bold">{formatearCOP(total)}</span>
        </p>

        {cuentas.map((c) => (
          <label key={c.id} className="flex items-center justify-between gap-3">
            <span className="font-semibold">{c.nombre}</span>
            <NumeroEnLinea
              ancho="largo"
              plata
              min={0}
              valor={reparto[c.id] ?? null}
              onValor={(n) => setReparto((previo) => ({ ...previo, [c.id]: n }))}
              lector={{ maxDigitos: DIGITOS_DE_PLATA }}
              aria-label={`Monto en ${c.nombre}`}
              data-e2e={`mixto-${c.nombre.toLowerCase().replace(/\s+/g, '-')}`}
            />
          </label>
        ))}

        <p className="flex justify-between border-t border-border pt-2 text-contenido" data-e2e="mixto-falta">
          <span className={falta === 0 ? 'text-success font-bold' : 'text-warning font-bold'}>
            {falta > 0 ? 'Falta por asignar' : falta < 0 ? 'Se pasó por' : 'Cuadra'}
          </span>
          <span className="num font-bold">{formatearCOP(Math.abs(falta))}</span>
        </p>
        {excedePagos(reparto) && (
          <MensajeError e2e="mixto-tope">⚠ Máximo {PAGOS_POR_VENTA_MAXIMO} formas de pago por venta.</MensajeError>
        )}

        <FilaAcciones>
          <Boton variante="contorno" onClick={cerrar}>
            Volver
          </Boton>
          <Boton
            disabled={!listo}
            onClick={() => {
              onAceptar(pagosDeReparto(reparto));
              setReparto({});
            }}
            data-e2e="mixto-aceptar"
          >
            Aceptar
          </Boton>
        </FilaAcciones>
      </div>
    </Modal>
  );
}
